(function(root, factory) {
  var api = factory(root);

  if (root) {
    root.DYOFCoverContract = api;
  }

  if (typeof module === "object" && module.exports) {
    module.exports = api;
  }
})(typeof globalThis !== "undefined" ? globalThis : this, function(root) {
  "use strict";

  // Translations (assets/dyof-i18n.js, non-English stores only). The English
  // stays here and is used as is when no language pack is loaded.
  function dyofInterpolate(text, params) {
    return params ? String(text).replace(/\{([A-Za-z0-9_]+)\}/g, function(match, name) {
      return Object.prototype.hasOwnProperty.call(params, name) && params[name] != null ? String(params[name]) : match;
    }) : text;
  }
  function dyofT(key, english, params) {
    var i18n = root.DYOFI18n;
    return i18n ? i18n.t(key, english, params) : dyofInterpolate(english, params);
  }
  function dyofPlural(key, count, english, params) {
    var i18n = root.DYOFI18n;
    var values = Object.assign({ count: count }, params || {});
    return i18n ? i18n.plural(key, count, english, params) : dyofInterpolate(Number(count) === 1 ? english.one : english.other, values);
  }

  var VERSION = 1;
  var MAX_TEXT_OBJECTS = 3;
  var MAX_IMAGE_OBJECTS = 3;
  var SIDES = ["front", "back"];
  var SHAPES = ["none", "circle", "square", "oval", "rectangle", "ribbon", "pill"];
  var MASKS = ["original", "square", "circle"];
  var FONT_IDS = ["minimal", "condensed", "casual", "lettering", "happy", "instrument-serif", "instrument-serif-italic"];
  var SEARCH_FILTER_IDS = [
    "filter:colour-minimal",
    "filter:floral-nature",
    "filter:quotes-messages",
    "filter:classic-elegant",
    "filter:love-positivity",
    "filter:pets",
    "filter:special-occasions"
  ];
  var COLOUR_FILTER_IDS = [
    "colour:black",
    "colour:white",
    "colour:blue",
    "colour:pink",
    "colour:green",
    "colour:red",
    "colour:purple",
    "colour:yellow",
    "colour:orange",
    "colour:brow"
  ];
  // Elastic closure is hidden from customers at the owner's request (Enes,
  // 2026-09-29: "it will be there in the future"). While this is false:
  // - the cover editor hides the "Elastic closure" section and the preview
  //   draws no band (dyof-cover-editor.js, elasticClosureOffered);
  // - Review hides its "Elastic closure" row (dyof-configurator.js);
  // - normalizeCover turns every elastic choice off, so a design saved or
  //   restored with a band, and every save/cart payload, carries
  //   { enabled: false } and cannot order one.
  // The colours, labels, band photos, editor/preview code and backend support
  // are kept. To bring the option back, set this to true. The switch-on tests
  // keep passing; these hidden-state tests then fail by design, so retire
  // them or flip them to expect the option:
  // - tests/sep29-elastic-hidden.test.js: "the elastic closure switch ships
  //   off, ...", "a design saved with an elastic band restores, saves and
  //   orders without one", "the front cover editor has no Elastic closure
  //   section while hidden", and the first check (the shipped contract) of
  //   "the cover preview draws no elastic band while hidden, ...";
  // - tests/wp5-cover-personalisation.test.js: "WP5 elastic is hidden: ...";
  // - tests/dyof-state.test.js: "covered-product Review summary hides the
  //   elastic closure row while the option is hidden".
  var ELASTIC_CLOSURE_OFFERED = false;
  var ELASTIC_IDS = [
    "navy", "tan", "pink", "golden-yellow", "black",
    "grey-green", "purple", "red", "silver-metallic", "gold-metallic"
  ];
  var ELASTIC_COLOURS = {
    navy: "#072340",
    tan: "#CAA285",
    pink: "#CB4B97",
    "golden-yellow": "#EDAA00",
    black: "#1E1E1E",
    "grey-green": "#535A54",
    purple: "#492A91",
    red: "#C50024",
    "silver-metallic": "#ADADAD",
    "gold-metallic": "#DFC15C"
  };
  // Effective-resolution cut-offs approved by the owner (Enes, 2026-09-27) as
  // final without printer confirmation. The one shared source is
  // existing_backend/src/cover/print-resolution-policy.json; this storefront
  // mirror is parity-tested against it and the server geometry.
  var PRINT_RESOLUTION = { warnEffectiveDpi: 150, stopEffectiveDpi: 100 };
  var POINTS_PER_MM = 72 / 25.4;
  var MIN_IMAGE_SCALE = 0.2;
  // Printed faces (existing_backend/src/cover/templates.ts) and the preview
  // width foreground boxes are authored against (render.ts previewObjectScale).
  // wrapWidthPt/wrapHeightPt (the printed full wrap) and previewCrop (the
  // default Shopify preview crop) are the server's (templates.ts) and are read
  // only by the back-logo geometry below.
  var PRINT_FACES = {
    clipbookA5: { wrapWidthPt: 1097.01, wrapHeightPt: 606.614, previewCrop: { width: 825, height: 1065 }, widthMm: 185, heightMm: 212, safeInsetMm: 10, bindingZoneMm: 38.3, previewCoverWidth: 625 },
    plannerA5: { wrapWidthPt: 1034.9, wrapHeightPt: 632.412, previewCrop: { width: 644, height: 856 }, widthMm: 185, heightMm: 212, safeInsetMm: 10, bindingZoneMm: 0, previewCoverWidth: 579 },
    personal: { wrapWidthPt: 813.543, wrapHeightPt: 498.898, previewCrop: { width: 567, height: 750 }, widthMm: 135, heightMm: 175, safeInsetMm: 10, bindingZoneMm: 41.56, previewCoverWidth: 588 }
  };
  var LEGACY_FULL_WRAP_CROP_ASPECT = 1.82;
  var PERSONAL_FULL_WRAP_CROP_ASPECT = 54 / 35;
  // The Filofax wordmark printed on the back of A5 and Personal Clipbook
  // covers, as the old builder did (Enes, 2026-10-16: "logos on the back like
  // they had them before ... the logo color should compliment the designs").
  // The path is lifted from Filofax's own Clipbook print artwork
  // (existing_backend/src/cover/back-logo-wordmark.json, with provenance);
  // boxes are that artwork's logo boxes in points from the full wrap's
  // bottom-left, and backPanelWidthPt is the back panel up to the first fold
  // guide. The A5 Planner has none. The server mirror is
  // existing_backend/src/cover/back-logo.ts (parity-tested). version is the
  // saved form's (intent.cover.backLogo.version); ruleVersion the colour
  // rule's (chooseBackLogoColour).
  var BACK_LOGO = {
    version: 1,
    ruleVersion: 2,
    minimumContrast: 3,
    minimumCoverage: 0.75,
    minimumAccentChroma: 15,
    wordmark: {
      width: 85.64,
      height: 24.564,
      pathSha256: "38a2a646c64fbd928612af36cc3b962baf04e56260f25294191645bc6a36e993",
      path: "M83.708 16.798L84.236 16.798C84.424 16.798 84.561 16.816 84.649 16.852C84.805 16.917 84.883 17.045 84.883 17.235C84.883 17.369 84.833 17.469 84.735 17.532C84.682 17.566 84.609 17.591 84.515 17.605C84.634 17.624 84.721 17.674 84.776 17.753C84.831 17.833 84.858 17.912 84.858 17.988L84.858 18.098C84.858 18.133 84.86 18.171 84.862 18.211C84.865 18.251 84.869 18.277 84.875 18.289L84.885 18.308L84.635 18.308C84.634 18.303 84.633 18.298 84.632 18.293C84.63 18.288 84.629 18.282 84.628 18.276L84.622 18.227L84.622 18.108C84.622 17.933 84.575 17.817 84.48 17.761C84.424 17.728 84.325 17.712 84.184 17.712L83.974 17.712L83.974 18.308L83.708 18.308ZM84.622 17.257C84.622 17.147 84.589 17.073 84.522 17.036C84.455 16.998 84.348 16.98 84.201 16.98L83.974 16.98L83.974 17.527L84.214 17.527C84.326 17.527 84.411 17.516 84.467 17.493C84.57 17.452 84.622 17.373 84.622 17.257M85.64 17.557C85.64 17.938 85.505 18.263 85.237 18.533C84.968 18.803 84.644 18.938 84.263 18.938C83.882 18.938 83.557 18.803 83.29 18.533C83.024 18.264 82.891 17.939 82.891 17.557C82.891 17.178 83.025 16.855 83.294 16.586C83.561 16.319 83.884 16.185 84.263 16.185C84.644 16.185 84.968 16.319 85.237 16.586C85.505 16.854 85.64 17.177 85.64 17.557M84.263 16.371C83.937 16.371 83.658 16.487 83.427 16.719C83.197 16.95 83.082 17.23 83.082 17.557C83.082 17.887 83.197 18.168 83.425 18.4C83.655 18.632 83.934 18.748 84.263 18.748C84.591 18.748 84.87 18.632 85.1 18.4C85.33 18.168 85.445 17.887 85.445 17.557C85.445 17.23 85.33 16.95 85.1 16.719C84.869 16.487 84.59 16.371 84.263 16.371M31.153 8.03C31.499 7.044 32.598 7.034 32.598 7.034L32.762 6.585L27.356 6.585L27.193 7.034C27.193 7.034 28.438 7.107 28.056 8.118L24.679 17.36C24.219 18.437 23.2 18.467 23.2 18.467L23.034 18.918L31.421 18.918L32.265 16.625L31.73 16.625C30.677 18.042 30.322 18.05 29.492 18.05L27.514 18.05ZM37.546 12.934C38.275 9.695 40.572 6.911 42.546 6.911C44.52 6.911 45.583 9.326 44.853 12.565C44.124 15.805 41.88 18.641 39.906 18.641C37.931 18.641 36.816 16.173 37.546 12.934M39.78 19.2C44.219 19.197 46.907 16.324 47.705 12.776C48.504 9.228 46.457 6.351 42.672 6.352C38.4 6.354 35.545 9.228 34.746 12.776C33.948 16.324 35.854 19.202 39.78 19.2M17.643 0.17C16.129 0 12.556 0.564 10.442 6.43L8.163 6.43L7.867 7.141C7.867 7.141 8.354 7.128 8.697 7.141C9.565 7.174 9.918 7.836 9.74 8.329C8.157 12.726 7.057 15.96 6.327 18.029C5.616 20.044 5.199 20.912 4.33 22.255C3.303 23.842 1.895 23.677 1.895 23.677C1.895 23.677 2.13 23.071 1.419 22.927C0.642 22.769 0 24.329 1.542 24.447C3.057 24.564 6.622 24.038 9.06 18.167C10.347 15.068 11.805 10.904 12.667 8.471C13.002 7.523 13.713 7.181 14.285 7.181L15.214 7.181L15.53 6.43L13.365 6.43C13.894 5.075 14.31 3.835 15.233 2.243C16.162 0.644 17.327 0.94 17.327 0.94C17.327 0.94 17.12 1.569 17.84 1.69C18.624 1.822 19.229 0.347 17.643 0.17M66.378 13.421C65.819 11.315 65.24 9.137 65.24 9.137L62.934 13.421ZM80.842 7.703C79.8 8.995 79.087 10.161 78.03 11.661L80.334 17.36C80.907 18.582 81.746 18.467 81.746 18.467L81.93 18.918L77.058 18.918L76.872 18.467C76.872 18.467 77.954 18.389 77.551 17.386L76.205 14.074C75.757 14.634 75.289 15.205 74.855 15.724C72.8 18.187 70.451 18.914 69.189 18.914L66.702 18.914L66.482 18.488C67.43 18.356 67.576 17.964 67.392 17.253C67.321 16.978 66.982 15.7 66.594 14.238L62.495 14.238L60.799 17.388C60.228 18.477 61.605 18.469 61.605 18.469L61.361 18.921L58.169 18.921L58.412 18.469C58.412 18.469 59.132 18.585 59.848 17.363L65.62 6.574L67.227 6.574C67.227 6.574 69.811 16.341 69.995 17.026C70.179 17.711 69.997 18.128 69.997 18.128C69.997 18.128 71.603 18.082 73.067 15.969C73.806 14.903 74.677 13.663 75.547 12.454L73.773 8.088C73.278 6.898 72.446 7.034 72.446 7.034L72.263 6.585L77.177 6.585L77.358 7.034C77.358 7.034 76.096 6.892 76.464 7.787L77.357 9.995C77.991 9.156 78.565 8.43 79.008 7.923C80.027 6.761 81.766 5.028 83.609 5.134L83.305 5.767C83.305 5.767 82.323 5.865 80.842 7.703M52.685 6.585L52.523 7.034C52.523 7.034 53.767 7.107 53.386 8.118L50.008 17.36C49.485 18.582 48.53 18.467 48.53 18.467L48.364 18.918L53.616 18.918L53.783 18.467C53.783 18.467 52.542 18.389 52.905 17.386L54.658 12.622L57.406 12.622L57.786 11.725L54.988 11.725L56.54 7.508L59.173 7.508C59.884 7.508 60.333 7.76 59.859 9.076L60.371 9.076L61.385 6.585ZM24.07 6.585L23.906 7.034C23.906 7.034 22.807 7.044 22.462 8.03L19.04 17.386C18.677 18.389 19.904 18.487 19.904 18.487L19.752 18.918L14.342 18.918L14.508 18.467C14.508 18.467 15.53 18.437 15.987 17.36L19.364 8.118C19.746 7.107 18.501 7.034 18.501 7.034L18.664 6.585Z"
    },
    boxes: {
      clipbookA5: { x: 179.308, y: 41.525, scale: 1, backPanelWidthPt: 442.196 },
      personal: { x: 117.055, y: 41.998, scale: 5 / 6, backPanelWidthPt: 303.3076 }
    }
  };
  var WHITE = "#FFFFFF";
  var ELASTIC_LABELS = {
    navy: dyofT("covercontract.elastic.navy", "Blue Marine"),
    tan: dyofT("covercontract.elastic.tan", "Beige"),
    pink: dyofT("covercontract.elastic.pink", "Fuchsia"),
    "golden-yellow": dyofT("covercontract.elastic.golden_yellow", "Yellow"),
    black: dyofT("covercontract.elastic.black", "Black"),
    "grey-green": dyofT("covercontract.elastic.grey_green", "NATO Green"),
    purple: dyofT("covercontract.elastic.purple", "Violet"),
    red: dyofT("covercontract.elastic.red", "Red"),
    "silver-metallic": dyofT("covercontract.elastic.silver_metallic", "Silver"),
    "gold-metallic": dyofT("covercontract.elastic.gold_metallic", "Gold")
  };

  function own(object, key) {
    return Object.prototype.hasOwnProperty.call(object, key);
  }

  function isObject(value) {
    return value && typeof value === "object" && !Array.isArray(value);
  }

  function clone(value) {
    var result;
    var key;
    if (Array.isArray(value)) {
      return value.map(clone);
    }
    if (!isObject(value)) {
      return value;
    }
    result = {};
    for (key in value) {
      if (own(value, key)) {
        result[key] = clone(value[key]);
      }
    }
    return result;
  }

  function merge(base, patch) {
    var result = clone(base || {});
    var key;
    if (!isObject(patch)) {
      return result;
    }
    for (key in patch) {
      if (!own(patch, key)) {
        continue;
      }
      result[key] = isObject(result[key]) && isObject(patch[key])
        ? merge(result[key], patch[key])
        : clone(patch[key]);
    }
    return result;
  }

  function clamp(value, minimum, maximum, fallback) {
    var number = Number(value);
    if (!Number.isFinite(number)) {
      number = fallback;
    }
    return Math.min(maximum, Math.max(minimum, number));
  }

  function colour(value, fallback) {
    var result = String(value || "").toUpperCase();
    return /^#[0-9A-F]{6}$/.test(result) ? result : fallback;
  }

  function cleanId(value) {
    return String(value || "").replace(/[^a-zA-Z0-9:_-]/g, "").slice(0, 100);
  }

  function normalizeFilter(value, allowed) {
    var normalized = String(value || "all").slice(0, 80);
    return normalized === "all" || allowed.indexOf(normalized) !== -1
      ? normalized
      : "all";
  }

  function createId(prefix) {
    var random;
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
      random = crypto.randomUUID();
    } else {
      random = Date.now().toString(36) + Math.random().toString(36).slice(2);
    }
    return prefix + "-" + random;
  }

  function normalizeTransform(input) {
    var source = isObject(input) ? input : {};
    return {
      x: clamp(source.x, 0, 1, 0.5),
      y: clamp(source.y, 0, 1, 0.5),
      scale: clamp(source.scale, 0.2, 3, 1),
      rotation: clamp(source.rotation, -180, 180, 0)
    };
  }

  function normalizeCrop(input) {
    var source = isObject(input) ? input : {};
    var crop = {
      x: clamp(source.x, 0, 1, 0.5),
      y: clamp(source.y, 0, 1, 0.5),
      zoom: clamp(source.zoom, 1, 8, 1),
      rotation: clamp(source.rotation, -180, 180, 0)
    };
    if (Number.isFinite(Number(source.width)) && Number.isFinite(Number(source.height))) {
      crop.width = clamp(source.width, 0.01, 1, 1);
      crop.height = clamp(source.height, 0.01, 1, 1);
    }
    return crop;
  }

  function normalizeLine(input) {
    var source = isObject(input) ? input : {};
    var fontId = FONT_IDS.indexOf(source.fontId) !== -1 ? source.fontId : "minimal";
    return {
      text: String(source.text || "").slice(0, 48),
      fontId: fontId,
      fontSize: clamp(source.fontSize, 8, 72, 24),
      colour: colour(source.colour, "#17545B")
    };
  }

  function createTextObject(input) {
    var source = isObject(input) ? input : {};
    return normalizeObject(merge({
      id: createId("text"),
      type: "text",
      name: "Text block",
      lines: [
        { text: "", fontId: "minimal", fontSize: 24, colour: "#17545B" },
        { text: "", fontId: "minimal", fontSize: 24, colour: "#17545B" },
        { text: "", fontId: "minimal", fontSize: 24, colour: "#17545B" }
      ],
      shape: "none",
      shapeColour: "#E7D1FD",
      transform: { x: 0.5, y: 0.42, scale: 1, rotation: 0 },
      overflowWarningFor: null,
      overflowAcceptedFor: null
    }, source), 0);
  }

  function createImageObject(assetId, input) {
    var source = isObject(input) ? input : {};
    return normalizeObject(merge({
      id: createId("image"),
      type: "image",
      name: "Image",
      assetId: cleanId(assetId),
      activeMask: "original",
      crops: {
        original: { x: 0.5, y: 0.5, zoom: 1, rotation: 0 },
        square: { x: 0.5, y: 0.5, zoom: 1, rotation: 0 },
        circle: { x: 0.5, y: 0.5, zoom: 1, rotation: 0 }
      },
      transform: { x: 0.5, y: 0.58, scale: 1, rotation: 0 },
      qualityWarning: null
    }, source), 0);
  }

  function normalizeObject(input, index) {
    var source = isObject(input) ? input : {};
    var type = source.type === "image" ? "image" : "text";
    var result = {
      id: cleanId(source.id) || createId(type),
      type: type,
      name: String(source.name || (type === "text" ? "Text block" : "Image")).slice(0, 60),
      transform: normalizeTransform(source.transform),
      order: index
    };
    if (type === "text") {
      result.lines = [0, 1, 2].map(function(lineIndex) {
        return normalizeLine(Array.isArray(source.lines) ? source.lines[lineIndex] : null);
      });
      result.shape = SHAPES.indexOf(source.shape) !== -1 ? source.shape : "none";
      result.shapeColour = colour(source.shapeColour, "#E7D1FD");
      result.shapeScale = clamp(source.shapeScale, 0.2, 3, result.transform.scale);
      result.transform.scale = 1;
      result.overflowWarningFor = cleanId(source.overflowWarningFor) || null;
      result.overflowAcceptedFor = cleanId(source.overflowAcceptedFor) || null;
      return result;
    }
    result.assetId = cleanId(source.assetId);
    result.activeMask = MASKS.indexOf(source.activeMask) !== -1 ? source.activeMask : "original";
    result.crops = {};
    MASKS.forEach(function(mask) {
      result.crops[mask] = normalizeCrop(source.crops && source.crops[mask]);
    });
    result.qualityWarning = source.qualityWarning ? String(source.qualityWarning).slice(0, 180) : null;
    return result;
  }

  function normalizeSide(input) {
    var source = isObject(input) ? input : {};
    var textCount = 0;
    var imageCount = 0;
    var seen = {};
    var objects = (Array.isArray(source.objects) ? source.objects : []).map(normalizeObject).filter(function(object) {
      if (!object.id || seen[object.id]) {
        return false;
      }
      if (object.type === "text") {
        textCount += 1;
        if (textCount > MAX_TEXT_OBJECTS) {
          return false;
        }
      } else {
        imageCount += 1;
        if (imageCount > MAX_IMAGE_OBJECTS) {
          return false;
        }
      }
      seen[object.id] = true;
      return true;
    });
    objects.forEach(function(object, index) {
      object.order = index;
    });
    return {
      textEnabled: source.textEnabled !== false,
      objects: objects
    };
  }

  function createDefaultCover() {
    return {
      version: VERSION,
      source: "catalogue",
      background: null,
      elastic: { enabled: false, colourId: null },
      filters: { theme: "all", colour: "all" },
      front: { textEnabled: true, objects: [] },
      back: { textEnabled: true, objects: [] },
      activeObject: { side: "front", id: null }
    };
  }

  function normalizeBackground(input) {
    if (!isObject(input)) {
      return null;
    }
    var source = input.source === "upload" ? "upload" : "catalogue";
    var reference = source === "upload" ? cleanId(input.assetId) : cleanId(input.designId);
    if (!reference) {
      return null;
    }
    var background = source === "upload"
      ? {
          source: source,
          assetId: reference,
          title: String(input.title || "Uploaded cover").slice(0, 100),
          crop: normalizeCrop(input.crop)
        }
      : { source: source, designId: reference, title: String(input.title || "Cover design").slice(0, 100) };
    if (source === "upload" && input.geometryVersion === 2) {
      background.geometryVersion = 2;
    }
    return background;
  }

  function normalizeCover(input) {
    var source = merge(createDefaultCover(), isObject(input) ? input : {});
    var active = isObject(source.activeObject) ? source.activeObject : {};
    var elasticEnabled = ELASTIC_CLOSURE_OFFERED
      && Boolean(source.elastic && source.elastic.enabled);
    var colourId = ELASTIC_IDS.indexOf(source.elastic && source.elastic.colourId) !== -1
      ? source.elastic.colourId
      : "navy";
    return {
      version: VERSION,
      source: source.source === "upload" ? "upload" : "catalogue",
      background: normalizeBackground(source.background),
      elastic: {
        enabled: elasticEnabled,
        colourId: elasticEnabled ? colourId : null
      },
      filters: {
        theme: normalizeFilter(
          source.filters && source.filters.theme,
          SEARCH_FILTER_IDS
        ),
        colour: normalizeFilter(
          source.filters && source.filters.colour,
          COLOUR_FILTER_IDS
        )
      },
      front: normalizeSide(source.front),
      back: normalizeSide(source.back),
      activeObject: {
        side: SIDES.indexOf(active.side) !== -1 ? active.side : "front",
        id: cleanId(active.id) || null
      }
    };
  }

  function findObject(side, objectId) {
    return side.objects.filter(function(object) { return object.id === objectId; })[0] || null;
  }

  function updateObject(side, objectId, patch) {
    side.objects = side.objects.map(function(object, index) {
      return object.id === objectId ? normalizeObject(merge(object, patch || {}), index) : object;
    });
  }

  function moveObject(side, objectId, direction) {
    var index = side.objects.map(function(object) { return object.id; }).indexOf(objectId);
    var nextIndex = Math.min(side.objects.length - 1, Math.max(0, index + direction));
    var object;
    if (index < 0 || index === nextIndex) {
      return;
    }
    object = side.objects.splice(index, 1)[0];
    side.objects.splice(nextIndex, 0, object);
    side.objects.forEach(function(item, order) { item.order = order; });
  }

  function applyAction(coverInput, action) {
    var cover = normalizeCover(coverInput);
    var payload = action && action.payload || {};
    var sideName = SIDES.indexOf(payload.side) !== -1 ? payload.side : "front";
    var side = cover[sideName];
    var object;
    switch (action && action.type) {
      case "COVER_SET_SOURCE":
        cover.source = payload === "upload" ? "upload" : "catalogue";
        if (cover.background && cover.background.source !== cover.source) {
          cover.background = null;
        }
        break;
      case "COVER_SET_BACKGROUND":
        cover.background = normalizeBackground(payload);
        cover.source = cover.background ? cover.background.source : cover.source;
        break;
      case "COVER_UPDATE_BACKGROUND":
        if (cover.background && cover.background.source === "upload") {
          cover.background = normalizeBackground(merge(cover.background, payload || {}));
        }
        break;
      case "COVER_SET_FILTERS":
        cover.filters = merge(cover.filters, payload || {});
        break;
      case "COVER_SET_ELASTIC":
        cover.elastic = payload && payload.enabled === false
          ? { enabled: false, colourId: null }
          : merge(cover.elastic, payload || {});
        break;
      case "COVER_SET_TEXT_ENABLED":
        side.textEnabled = payload.enabled !== false;
        break;
      case "COVER_ADD_TEXT":
        if (side.objects.filter(function(item) { return item.type === "text"; }).length < MAX_TEXT_OBJECTS) {
          object = createTextObject(payload.object);
          side.objects.push(object);
          cover.activeObject = { side: sideName, id: object.id };
        }
        break;
      case "COVER_ADD_IMAGE":
        if (side.objects.filter(function(item) { return item.type === "image"; }).length < MAX_IMAGE_OBJECTS && payload.assetId) {
          object = createImageObject(payload.assetId, payload.object);
          side.objects.push(object);
          cover.activeObject = { side: sideName, id: object.id };
        }
        break;
      case "COVER_UPDATE_OBJECT":
        updateObject(side, cleanId(payload.id), payload.patch);
        if (payload.clearSelection) {
          cover.activeObject = { side: sideName, id: null };
        } else if (payload.selectObject) {
          cover.activeObject = { side: sideName, id: cleanId(payload.id) || null };
        }
        break;
      case "COVER_REMOVE_OBJECT":
        side.objects = side.objects.filter(function(item) { return item.id !== cleanId(payload.id); });
        if (cover.activeObject.id === cleanId(payload.id)) {
          cover.activeObject = { side: sideName, id: null };
        }
        break;
      case "COVER_MOVE_OBJECT":
        moveObject(side, cleanId(payload.id), payload.direction === "backward" ? -1 : 1);
        break;
      case "COVER_SELECT_OBJECT":
        cover.activeObject = findObject(side, cleanId(payload.id))
          ? { side: sideName, id: cleanId(payload.id) }
          : { side: sideName, id: null };
        break;
      default:
        break;
    }
    return normalizeCover(cover);
  }

  function validateCover(coverInput, options) {
    var cover = normalizeCover(coverInput);
    var required = !options || options.required !== false;
    var errors = [];
    var warnings = [];
    // Resolution is derived from the live crop and the asset pixels whenever the
    // caller supplies them; a stored upload-time qualityWarning is then stale.
    var resolution = options && options.printResolution
      ? printResolution(cover, options.printResolution)
      : [];
    var measured = {};
    if (required && !cover.background) {
      errors.push(dyofT("covercontract.validation.design_required", "Choose or upload a cover design."));
    }
    resolution.forEach(function(entry) {
      if (entry.objectId) {
        measured[entry.objectId] = true;
      }
      if (entry.status === "stop") {
        errors.push(entry.message);
      } else if (entry.status === "warn") {
        warnings.push(entry.message);
      }
    });
    SIDES.forEach(function(sideName) {
      cover[sideName].objects.forEach(function(object) {
        if (object.type === "image" && !object.assetId) {
          errors.push(dyofT("covercontract.validation.image_missing", "An image on the {side} cover is missing its uploaded file.", { side: sideName === "front" ? dyofT("covercontract.side.front", "front") : dyofT("covercontract.side.back", "back") }));
        }
        if (object.type === "text" && object.overflowWarningFor) {
          if (object.overflowAcceptedFor !== object.overflowWarningFor) {
            errors.push(dyofT("covercontract.validation.text_overflow", "Choose whether to keep or adjust the text that extends beyond its shape on the {side} cover.", { side: sideName === "front" ? dyofT("covercontract.side.front", "front") : dyofT("covercontract.side.back", "back") }));
          } else {
            warnings.push(dyofT("covercontract.validation.print_confirmed", "{side} cover: print as shown confirmed.", { side: sideName === "front" ? dyofT("covercontract.side.front_title", "Front") : dyofT("covercontract.side.back_title", "Back") }));
          }
        }
        if (object.qualityWarning && !measured[object.id]) {
          warnings.push(object.qualityWarning);
        }
      });
    });
    return { valid: errors.length === 0, errors: errors, warnings: warnings, printResolution: resolution };
  }

  function projectSide(sideInput) {
    var side = normalizeSide(sideInput);
    return {
      textEnabled: side.textEnabled,
      objects: side.objects.filter(function(object) {
        return object.type !== "text" || side.textEnabled;
      }).map(function(object) {
        var result = clone(object);
        delete result.order;
        return result;
      })
    };
  }

  function projectOrderCover(coverInput) {
    var cover = normalizeCover(coverInput);
    return {
      version: cover.version,
      background: clone(cover.background),
      elastic: cover.elastic.enabled
        ? clone(cover.elastic)
        : { enabled: false },
      front: projectSide(cover.front),
      back: projectSide(cover.back)
    };
  }

  function printFace(context) {
    if (context && context.size === "personal") {
      return PRINT_FACES.personal;
    }
    return context && context.productType === "planner" ? PRINT_FACES.plannerA5 : PRINT_FACES.clipbookA5;
  }

  function printResolutionStatus(effectiveDpi) {
    if (effectiveDpi < PRINT_RESOLUTION.stopEffectiveDpi) {
      return "stop";
    }
    return effectiveDpi < PRINT_RESOLUTION.warnEffectiveDpi ? "warn" : "ok";
  }

  function printSource(asset) {
    if (!asset || (asset.mime !== "image/jpeg" && asset.mime !== "image/png")) {
      return null;
    }
    var width = Number(asset.width);
    var height = Number(asset.height);
    return width > 0 && height > 0 ? { width: width, height: height } : null;
  }

  // Same arithmetic, in the same order, as the server's fullWrapCropWindow and
  // full-wrap placements (existing_backend/src/cover/geometry.ts); x/y never
  // change the printed scale, so only the window size is needed here.
  function backgroundEffectiveDpi(background, source, context) {
    var face = printFace(context);
    var faceWidth = face.widthMm * POINTS_PER_MM;
    var faceHeight = face.heightMm * POINTS_PER_MM;
    var physical = background.geometryVersion === 2;
    var crop = background.crop || {};
    var aspectRatio = physical
      ? faceWidth * 2 / faceHeight
      : context && context.size === "personal" ? PERSONAL_FULL_WRAP_CROP_ASPECT : LEGACY_FULL_WRAP_CROP_ASPECT;
    var sourceAspect = source.width / source.height;
    var baseWidth = sourceAspect >= aspectRatio ? source.height * aspectRatio : source.width;
    var baseHeight = sourceAspect >= aspectRatio ? source.height : source.width / aspectRatio;
    var zoom = Math.min(8, Math.max(1, crop.zoom));
    var windowWidth = !physical && crop.width
      ? Math.min(source.width, Math.max(1, crop.width * source.width))
      : baseWidth / zoom;
    var windowHeight = !physical && crop.height
      ? Math.min(source.height, Math.max(1, crop.height * source.height))
      : baseHeight / zoom;
    var scaleX = faceWidth * 2 / windowWidth;
    var scaleY = physical ? scaleX : faceHeight / windowHeight;
    return 72 * Math.min(source.width / (source.width * scaleX), source.height / (source.height * scaleY));
  }

  // Mirrors the server's coverImageObjectBox and drawImageObject scale.
  function imageEffectiveDpi(object, sideName, source, context) {
    var face = printFace(context);
    var mask = object.activeMask;
    var crop = object.crops[mask];
    var hasCropRect = crop.width != null && crop.height != null;
    var cropRatio = hasCropRect
      ? (crop.width * source.width) / (crop.height * source.height)
      : source.width / source.height;
    var pageWidth = face.widthMm * POINTS_PER_MM;
    var pageHeight = face.heightMm * POINTS_PER_MM;
    var previewScale = pageWidth / face.previewCoverWidth;
    var baseBoxSize = (mask === "original" ? 277 : 220) * previewScale;
    var baseBoxWidth = baseBoxSize;
    var baseBoxHeight = baseBoxSize;
    if (mask === "original") {
      if (cropRatio >= 1) {
        baseBoxHeight = baseBoxWidth / cropRatio;
      } else {
        baseBoxWidth = baseBoxHeight * cropRatio;
      }
    }
    var inset = face.safeInsetMm * POINTS_PER_MM;
    var bindingInset = (face.bindingZoneMm + face.safeInsetMm) * POINTS_PER_MM;
    var safeWidth = pageWidth - inset - bindingInset;
    var safeHeight = pageHeight - inset * 2;
    var radians = object.transform.rotation * Math.PI / 180;
    var cosine = Math.abs(Math.cos(radians));
    var sine = Math.abs(Math.sin(radians));
    var rotatedBaseWidth = baseBoxWidth * cosine + baseBoxHeight * sine;
    var rotatedBaseHeight = baseBoxWidth * sine + baseBoxHeight * cosine;
    var maximumScale = Math.min(
      3,
      safeWidth / Math.max(1, rotatedBaseWidth),
      safeHeight / Math.max(1, rotatedBaseHeight)
    );
    var objectScale = Math.min(object.transform.scale, maximumScale);
    var boxWidth = baseBoxWidth * objectScale;
    var boxHeight = baseBoxHeight * objectScale;
    var pointsPerPixel = hasCropRect
      ? Math.max(boxWidth / (source.width * crop.width), boxHeight / (source.height * crop.height))
      : Math.max(boxWidth / source.width, boxHeight / source.height) * crop.zoom;
    return 72 / pointsPerPixel;
  }

  function printResolutionMessage(entry) {
    var dpi = Math.floor(entry.effectiveDpi);
    var background = entry.placement === "background";
    var side = entry.placement === "front"
      ? dyofT("covercontract.side.front", "front")
      : dyofT("covercontract.side.back", "back");
    if (entry.status === "stop") {
      return background
        ? dyofT("covercontract.resolution.background_stop", "Your cover background photo is too low-resolution to print at this crop (about {dpi} effective DPI; at least {minimum} needed). Zoom out or upload a larger photo.", { dpi: dpi, minimum: PRINT_RESOLUTION.stopEffectiveDpi })
        : dyofT("covercontract.resolution.photo_stop", "Your {side} cover photo is too low-resolution to print at this size (about {dpi} effective DPI; at least {minimum} needed). Make it smaller, crop it less tightly or upload a larger photo.", { side: side, dpi: dpi, minimum: PRINT_RESOLUTION.stopEffectiveDpi });
    }
    if (entry.status === "warn") {
      return background
        ? dyofT("covercontract.resolution.background_warn", "Your cover background photo may print softly at its current size (about {dpi} effective DPI; {recommended} DPI or more recommended).", { dpi: dpi, recommended: PRINT_RESOLUTION.warnEffectiveDpi })
        : dyofT("covercontract.resolution.photo_warn", "Your {side} cover photo may print softly at its current size (about {dpi} effective DPI; {recommended} DPI or more recommended).", { side: side, dpi: dpi, recommended: PRINT_RESOLUTION.warnEffectiveDpi });
    }
    return "";
  }

  /**
   * Effective DPI of each uploaded photo placement at its real print size:
   * source pixels covered by the crop / printed inches of that area. context =
   * { size, productType, assets: Map or object of { mime, width, height } by
   * asset ID (displayed, EXIF-oriented pixels), placements?: ["background",
   * "front", "back"] }. Placements whose asset is not known are omitted.
   */
  function printResolution(coverInput, context) {
    var cover = normalizeCover(coverInput);
    var assets = context && context.assets;
    var placements = context && Array.isArray(context.placements) ? context.placements : ["background", "front", "back"];
    var results = [];
    function sourceFor(assetId) {
      if (!assets) {
        return null;
      }
      return printSource(typeof assets.get === "function" ? assets.get(assetId) : assets[assetId]);
    }
    function add(entry) {
      entry.status = printResolutionStatus(entry.effectiveDpi);
      entry.message = printResolutionMessage(entry);
      results.push(entry);
    }
    var background = cover.background;
    var backgroundSource = background && background.source === "upload" ? sourceFor(background.assetId) : null;
    if (backgroundSource && placements.indexOf("background") !== -1) {
      add({ placement: "background", assetId: background.assetId, effectiveDpi: backgroundEffectiveDpi(background, backgroundSource, context) });
    }
    SIDES.forEach(function(sideName) {
      if (placements.indexOf(sideName) === -1) {
        return;
      }
      cover[sideName].objects.forEach(function(object) {
        var source = object.type === "image" ? sourceFor(object.assetId) : null;
        if (source) {
          add({ placement: sideName, objectId: object.id, assetId: object.assetId, effectiveDpi: imageEffectiveDpi(object, sideName, source, context) });
        }
      });
    });
    return results;
  }

  /**
   * Upload-time rule: reject only a photo that cannot reach the stop cut-off
   * even at its loosest use - a background filled edge to edge at zoom 1, or a
   * foreground photo uncropped at the smallest Size in its best shape.
   */
  function uploadPrintCheck(purpose, dimensions, context) {
    var face = printFace(context);
    var asset = { mime: "image/jpeg", width: dimensions && dimensions.width, height: dimensions && dimensions.height };
    var source = printSource(asset);
    var loosest;
    var message;
    if (!source) {
      return { ok: false, effectiveDpi: 0, message: dyofT("covercontract.resolution.dimensions_unreadable", "The photo dimensions could not be read.") };
    }
    if (purpose === "background") {
      loosest = backgroundEffectiveDpi({ geometryVersion: 2, crop: { x: 0.5, y: 0.5, zoom: 1, rotation: 0 } }, source, context);
      message = dyofT("covercontract.resolution.background_too_small", "This photo is too small to print on the cover. Use a photo at least {width} × {height} pixels.", {
        width: Math.ceil(PRINT_RESOLUTION.stopEffectiveDpi * face.widthMm * 2 / 25.4),
        height: Math.ceil(PRINT_RESOLUTION.stopEffectiveDpi * face.heightMm / 25.4)
      });
    } else {
      loosest = Math.max.apply(null, MASKS.map(function(mask) {
        return imageEffectiveDpi(createImageObject("upload-check", {
          activeMask: mask,
          transform: { x: 0.5, y: 0.5, scale: MIN_IMAGE_SCALE, rotation: 0 }
        }), "front", source, context);
      }));
      message = dyofT("covercontract.resolution.photo_too_small", "This photo is too small to print clearly. Use a photo at least {pixels} pixels on its longest side.", {
        pixels: Math.ceil(PRINT_RESOLUTION.stopEffectiveDpi * 277 * MIN_IMAGE_SCALE * face.widthMm / face.previewCoverWidth / 25.4)
      });
    }
    return loosest < PRINT_RESOLUTION.stopEffectiveDpi
      ? { ok: false, effectiveDpi: loosest, message: message }
      : { ok: true, effectiveDpi: loosest, message: "" };
  }

  // Back-logo geometry, mirroring the serving backend's
  // existing_backend/src/cover/geometry.ts. The cropper window of an uploaded
  // background (uploadBackgroundWindow): version 2 shows two editor faces side
  // by side; version 1 the legacy 1.82 (Personal 54:35) window or its stored
  // size.
  function uploadWindowAspect(face, background, context) {
    if (background.geometryVersion === 2) {
      return 2 * face.widthMm / face.heightMm;
    }
    return context && context.size === "personal" ? PERSONAL_FULL_WRAP_CROP_ASPECT : LEGACY_FULL_WRAP_CROP_ASPECT;
  }

  // Which wrap region the storefront showed as each face (server geometry.ts
  // coverFaceModel): a catalogue design's preview crop of the wrap, a version-2
  // upload's half of its cropper window spanning the wrap width, or a
  // version-1 upload's half of the wrap.
  function faceModel(background, context) {
    if (background && background.source === "upload") {
      return { source: background.geometryVersion === 2 ? "upload" : "legacy-upload" };
    }
    return { source: "catalogue", crop: context && context.catalogueCrop };
  }

  function printFaceSize(face, model) {
    if (model.source === "catalogue") {
      var crop = model.crop && model.crop.width > 0 && model.crop.height > 0 ? model.crop : face.previewCrop;
      return { width: face.wrapHeightPt * crop.width / crop.height, height: face.wrapHeightPt };
    }
    return {
      width: face.wrapWidthPt / 2,
      height: model.source === "upload" ? face.wrapWidthPt / (2 * face.widthMm / face.heightMm) : face.wrapHeightPt
    };
  }

  // Mirrors the server's uploadBackgroundWindow, fullWrapCropWindow and
  // uploadWrapPlacement (existing_backend/src/cover/geometry.ts): where an
  // uploaded background prints on the wrap, in PDF points (y up). source =
  // the photo's displayed { width, height } in pixels.
  function uploadBackgroundPlacement(backgroundInput, source, context) {
    var face = printFace(context);
    var background = isObject(backgroundInput) ? backgroundInput : {};
    var stored = background.crop || {};
    var crop = background.geometryVersion === 2
      ? { x: stored.x, y: stored.y, zoom: stored.zoom }
      : stored;
    var aspectRatio = uploadWindowAspect(face, background, context);
    var sourceAspect = source.width / source.height;
    var baseWidth = sourceAspect >= aspectRatio ? source.height * aspectRatio : source.width;
    var baseHeight = sourceAspect >= aspectRatio ? source.height : source.width / aspectRatio;
    var zoom = Math.min(8, Math.max(1, crop.zoom));
    var windowWidth = crop.width
      ? Math.min(source.width, Math.max(1, crop.width * source.width))
      : baseWidth / zoom;
    var windowHeight = crop.height
      ? Math.min(source.height, Math.max(1, crop.height * source.height))
      : baseHeight / zoom;
    var centerX = Math.min(source.width - windowWidth / 2, Math.max(windowWidth / 2, crop.x * source.width));
    var centerY = Math.min(source.height - windowHeight / 2, Math.max(windowHeight / 2, crop.y * source.height));
    var wrapAspect = face.wrapWidthPt / face.wrapHeightPt;
    var width = windowWidth;
    var height = width / wrapAspect;
    if (height > source.height) {
      height = source.height;
      width = height * wrapAspect;
    }
    var x = centerX - width / 2;
    var y = Math.min(source.height - height, Math.max(0, centerY - height / 2));
    var scale = face.wrapWidthPt / width;
    return {
      placement: {
        x: -x * scale,
        y: -(source.height - y - height) * scale,
        width: source.width * scale,
        height: source.height * scale
      },
      rotationCenter: { x: face.wrapWidthPt / 2, y: face.wrapHeightPt / 2 + (y - (centerY - height / 2)) * scale },
      rotation: clamp(stored.rotation, -180, 180, 0),
      wrapWidthPt: face.wrapWidthPt,
      wrapHeightPt: face.wrapHeightPt
    };
  }

  // The printed back logo box of a product (BACK_LOGO), or null when it prints
  // none (the A5 Planner). context = { size, productType }.
  function backLogoBox(context) {
    if (context && context.productType === "planner") {
      return null;
    }
    var catalogueKey = context && context.size === "personal" ? "personal" : "clipbookA5";
    var box = BACK_LOGO.boxes[catalogueKey];
    var face = PRINT_FACES[catalogueKey];
    return {
      catalogueKey: catalogueKey,
      x: box.x,
      y: box.y,
      width: BACK_LOGO.wordmark.width * box.scale,
      height: BACK_LOGO.wordmark.height * box.scale,
      scale: box.scale,
      backPanelWidthPt: box.backPanelWidthPt,
      wrapWidthPt: face.wrapWidthPt,
      wrapHeightPt: face.wrapHeightPt
    };
  }

  // The logo box as shares of the back face the editor shows (top-left
  // origin), for the face model of this background (faceModel above): the
  // preview draws it exactly where it prints relative to the artwork.
  function backLogoFaceBox(context, background) {
    var box = backLogoBox(context);
    if (!box) {
      return null;
    }
    var printed = printFaceSize(printFace(context), faceModel(background, context));
    var faceBottom = (box.wrapHeightPt - printed.height) / 2;
    return {
      x: box.x / printed.width,
      y: (faceBottom + printed.height - box.y - box.height) / printed.height,
      width: box.width / printed.width,
      height: box.height / printed.height
    };
  }

  function normalizeBackLogo(value) {
    var hex = colour(isObject(value) ? value.colour : value, "");
    return hex ? { version: BACK_LOGO.version, colour: hex } : null;
  }

  function hexChannels(hex) {
    var value = colour(hex, "");
    return value
      ? [parseInt(value.slice(1, 3), 16), parseInt(value.slice(3, 5), 16), parseInt(value.slice(5, 7), 16)]
      : null;
  }

  function channelsHex(channels) {
    return "#" + channels.map(function(value) {
      return ("0" + Math.round(Math.min(255, Math.max(0, value))).toString(16)).slice(-2);
    }).join("").toUpperCase();
  }

  function linearChannel(value) {
    var channel = value / 255;
    return channel <= 0.04045 ? channel / 12.92 : Math.pow((channel + 0.055) / 1.055, 2.4);
  }

  // WCAG 2 relative luminance and contrast ratio.
  function relativeLuminance(hex) {
    var channels = hexChannels(hex);
    return 0.2126 * linearChannel(channels[0]) + 0.7152 * linearChannel(channels[1]) + 0.0722 * linearChannel(channels[2]);
  }

  function contrastRatio(left, right) {
    var a = relativeLuminance(left);
    var b = relativeLuminance(right);
    return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
  }

  // CIELAB (D65), for tints, shades and saturation.
  var LAB_WHITE = [0.95047, 1, 1.08883];
  var LAB_EPSILON = 216 / 24389;
  var LAB_KAPPA = 24389 / 27;

  function labF(value) {
    return value > LAB_EPSILON ? Math.cbrt(value) : (LAB_KAPPA * value + 16) / 116;
  }

  function labFInverse(value) {
    var cube = value * value * value;
    return cube > LAB_EPSILON ? cube : (116 * value - 16) / LAB_KAPPA;
  }

  function channelsLab(channels) {
    var r = linearChannel(channels[0]);
    var g = linearChannel(channels[1]);
    var b = linearChannel(channels[2]);
    var fx = labF((0.4124564 * r + 0.3575761 * g + 0.1804375 * b) / LAB_WHITE[0]);
    var fy = labF((0.2126729 * r + 0.7151522 * g + 0.0721750 * b) / LAB_WHITE[1]);
    var fz = labF((0.0193339 * r + 0.1191920 * g + 0.9503041 * b) / LAB_WHITE[2]);
    return { L: 116 * fy - 16, a: 500 * (fx - fy), b: 200 * (fy - fz) };
  }

  function hexLab(hex) {
    return channelsLab(hexChannels(hex));
  }

  // Lab to sRGB hex; channels outside the sRGB gamut are clipped.
  function labHex(lab) {
    var fy = (lab.L + 16) / 116;
    var x = labFInverse(fy + lab.a / 500) * LAB_WHITE[0];
    var y = labFInverse(fy) * LAB_WHITE[1];
    var z = labFInverse(fy - lab.b / 200) * LAB_WHITE[2];
    return channelsHex([
      3.2404542 * x - 1.5371385 * y - 0.4985314 * z,
      -0.9692660 * x + 1.8760108 * y + 0.0415560 * z,
      0.0556434 * x - 0.2040259 * y + 1.0572252 * z
    ].map(function(value) {
      var linear = Math.min(1, Math.max(0, value));
      return 255 * (linear <= 0.0031308 ? 12.92 * linear : 1.055 * Math.pow(linear, 1 / 2.4) - 0.055);
    }));
  }

  function labChroma(lab) {
    return Math.sqrt(lab.a * lab.a + lab.b * lab.b);
  }

  // A pale tint of the background's own hue, like Filofax's own back logos
  // on strong colours (#E9E5E3, #FCE5EF, #E1F4FD): L* 94, chroma at most 8.
  function backLogoTint(background) {
    var lab = hexLab(background);
    var chroma = labChroma(lab);
    var share = chroma > 0 ? Math.min(8, chroma * 0.25) / chroma : 0;
    return labHex({ L: 94, a: lab.a * share, b: lab.b * share });
  }

  // The background's own colour made darker (or lighter) in L* steps, in
  // the order the rule tries them: away from the nearer end first (towards
  // black on a light background), then the other way.
  function backLogoShades(background) {
    var lab = hexLab(background);
    var darker = contrastRatio(background, "#000000") >= contrastRatio(background, WHITE);
    var shades = [];
    [darker ? -1 : 1, darker ? 1 : -1].forEach(function(direction) {
      for (var step = 1; step <= 100; step += 1) {
        var candidate = labHex({ L: Math.min(100, Math.max(0, lab.L + direction * step)), a: lab.a, b: lab.b });
        if (shades[shades.length - 1] !== candidate) {
          shades.push(candidate);
        }
      }
    });
    return shades;
  }

  // The colours behind the logo box as [{ colour, share }] (shares of the
  // box, in thousandths); a bare background stands for a box of one colour.
  function backLogoRegions(regions, background) {
    var valid = (Array.isArray(regions) ? regions : []).map(function(region) {
      return isObject(region)
        ? { colour: colour(region.colour, ""), share: Math.round(Number(region.share) * 1000) }
        : null;
    }).filter(function(region) {
      return region && region.colour && region.share > 0;
    });
    return valid.length ? valid : [{ colour: background, share: 1000 }];
  }

  // The share of the box (in thousandths) a colour reaches the minimum
  // contrast against.
  function backLogoCoverage(value, regions) {
    return regions.reduce(function(covered, region) {
      return covered + (contrastRatio(value, region.colour) >= BACK_LOGO.minimumContrast ? region.share : 0);
    }, 0);
  }

  /**
   * The one back-logo colour rule, shared by the policy generator, the
   * storefront and (mirrored) the server. input = { background: the box's
   * most common colour, regions: every colour behind the logo box with its
   * share ([{ colour, share }], backLogoSample; default the background
   * alone), palette: candidate colours (the design's colour table and the
   * back panel's dominant colours), handPick?: a colour Filofax chose for
   * this design }. A colour reads when it reaches the minimum WCAG contrast
   * (3:1) against at least 75% of the box, so a busy box is judged by every
   * colour behind the logo, not one average of them. In order:
   * a. Filofax's hand-pick;
   * b./c. strong or dark backgrounds: a pale tint of the background's hue, or
   *    white, that reads;
   * d. pale or busy backgrounds: the most saturated palette colour that
   *    reads, of CIELAB chroma 15 or more (the design's own accent, never a
   *    neutral black or grey);
   * e. otherwise a darker (or lighter) shade of the background that reads;
   * f. when no colour reads on this box, the candidate that reaches 3:1 on
   *    the largest share of it and stands out most on the rest
   *    ("best-coverage", flagged for review).
   * Returns { colour, rule, contrast: against the background, coverage:
   * the share of the box it reaches 3:1 on } or null without a background.
   */
  function chooseBackLogoColour(input) {
    var source = isObject(input) ? input : {};
    var background = colour(source.background, "");
    var handPick = colour(source.handPick, "");
    var regions = backLogoRegions(source.regions, background);
    function result(value, rule) {
      return {
        colour: value,
        rule: rule,
        contrast: background ? Math.round(contrastRatio(value, background) * 100) / 100 : null,
        coverage: background ? Math.min(1000, backLogoCoverage(value, regions)) / 1000 : null
      };
    }
    function reads(value) {
      return backLogoCoverage(value, regions) >= BACK_LOGO.minimumCoverage * 1000;
    }
    if (handPick) {
      return result(handPick, "filofax-pick");
    }
    if (!background) {
      return null;
    }
    var tint = backLogoTint(background);
    if (reads(tint)) {
      return result(tint, "pale-tint");
    }
    if (reads(WHITE)) {
      return result(WHITE, "white");
    }
    var palette = (Array.isArray(source.palette) ? source.palette : []).map(function(value) {
      return colour(value, "");
    }).filter(function(value, index, values) {
      return value && values.indexOf(value) === index;
    });
    var accents = palette.filter(function(value) {
      return labChroma(hexLab(value)) >= BACK_LOGO.minimumAccentChroma && reads(value);
    }).sort(function(left, right) {
      return labChroma(hexLab(right)) - labChroma(hexLab(left))
        || backLogoCoverage(right, regions) - backLogoCoverage(left, regions)
        || contrastRatio(right, background) - contrastRatio(left, background)
        || (left < right ? -1 : left > right ? 1 : 0);
    });
    if (accents.length) {
      return result(accents[0], "accent");
    }
    var shades = backLogoShades(background);
    var shade = shades.filter(reads)[0];
    if (shade) {
      return result(shade, "shade");
    }
    // Equal shares go to the colour that stands out most where it falls
    // short (its lowest contrast against any colour behind it), then to the
    // earlier candidate: the background's own tint first.
    function weakest(value) {
      return Math.min.apply(null, regions.map(function(region) { return contrastRatio(value, region.colour); }));
    }
    var best = [tint, WHITE].concat(palette, shades, ["#000000"]).reduce(function(chosen, candidate) {
      var gain = backLogoCoverage(candidate, regions) - backLogoCoverage(chosen, regions);
      return gain > 0 || (gain === 0 && weakest(candidate) > weakest(chosen)) ? candidate : chosen;
    });
    return result(best, "best-coverage");
  }

  // The pixels whose centres lie inside rect (pixel units), every step-th
  // pixel on both axes, as [r, g, b] triples.
  function regionPixels(image, rect, step) {
    var channels = image.channels || 4;
    var left = Math.max(0, Math.ceil(rect.x - 0.5));
    var top = Math.max(0, Math.ceil(rect.y - 0.5));
    var right = Math.min(image.width - 1, Math.floor(rect.x + rect.width - 0.5));
    var bottom = Math.min(image.height - 1, Math.floor(rect.y + rect.height - 0.5));
    var pixels = [];
    for (var row = top; row <= bottom; row += step) {
      for (var column = left; column <= right; column += step) {
        var offset = (row * image.width + column) * channels;
        pixels.push([image.data[offset], image.data[offset + 1], image.data[offset + 2]]);
      }
    }
    return pixels;
  }

  function median(values) {
    var sorted = values.slice().sort(function(left, right) { return left - right; });
    return sorted[Math.floor(sorted.length / 2)];
  }

  function meanChannels(pixels) {
    var sums = [0, 0, 0];
    pixels.forEach(function(pixel) {
      sums[0] += pixel[0];
      sums[1] += pixel[1];
      sums[2] += pixel[2];
    });
    return sums.map(function(sum) { return sum / pixels.length; });
  }

  function medianHex(pixels) {
    return channelsHex([0, 1, 2].map(function(channel) {
      return median(pixels.map(function(pixel) { return pixel[channel]; }));
    }));
  }

  // Median cut: the pixels in up to count groups, each split along its
  // widest channel.
  function medianCut(pixels, count) {
    var boxes = pixels.length ? [pixels] : [];
    while (boxes.length < count) {
      var best = -1;
      var bestRange = 0;
      var bestChannel = 0;
      boxes.forEach(function(box, index) {
        for (var channel = 0; channel < 3; channel += 1) {
          var minimum = 255;
          var maximum = 0;
          for (var item = 0; item < box.length; item += 1) {
            minimum = Math.min(minimum, box[item][channel]);
            maximum = Math.max(maximum, box[item][channel]);
          }
          if (maximum - minimum > bestRange) {
            bestRange = maximum - minimum;
            best = index;
            bestChannel = channel;
          }
        }
      });
      if (best < 0) {
        break;
      }
      var sorted = boxes[best].slice().sort(function(left, right) {
        return left[bestChannel] - right[bestChannel] || left[0] - right[0] || left[1] - right[1] || left[2] - right[2];
      });
      var middle = Math.floor(sorted.length / 2);
      boxes.splice(best, 1, sorted.slice(0, middle), sorted.slice(middle));
    }
    return boxes;
  }

  // Median cut: up to count dominant colours, most common first.
  function dominantColours(pixels, count) {
    var seen = {};
    return medianCut(pixels, count).map(function(box) {
      return { colour: channelsHex(meanChannels(box)), count: box.length };
    }).sort(function(left, right) {
      return right.count - left.count || (left.colour < right.colour ? -1 : left.colour > right.colour ? 1 : 0);
    }).map(function(entry) {
      return entry.colour;
    }).filter(function(value) {
      if (seen[value]) {
        return false;
      }
      seen[value] = true;
      return true;
    });
  }

  function deltaE(left, right) {
    return Math.sqrt(Math.pow(left.L - right.L, 2) + Math.pow(left.a - right.a, 2) + Math.pow(left.b - right.b, 2));
  }

  // The colours behind the logo, most common first: the box's pixels in up
  // to sixteen median-cut groups, each merged into a more common group within
  // ΔE 12 of it, then each the per-channel median of its pixels (a colour
  // the artwork has, never the average of a pattern's two colours) with its
  // share of the box (in thousandths, as the rule counts it).
  function boxRegions(pixels) {
    var groups = medianCut(pixels, 16).map(function(group) {
      var mean = meanChannels(group);
      return { pixels: group, lab: channelsLab(mean), hex: channelsHex(mean) };
    }).sort(function(left, right) {
      return right.pixels.length - left.pixels.length || (left.hex < right.hex ? -1 : left.hex > right.hex ? 1 : 0);
    });
    var merged = [];
    groups.forEach(function(group) {
      var target = merged.filter(function(region) { return deltaE(region.lab, group.lab) < 12; })[0];
      if (target) {
        target.pixels = target.pixels.concat(group.pixels);
      } else {
        merged.push({ lab: group.lab, pixels: group.pixels.slice() });
      }
    });
    return merged.map(function(region) {
      return { colour: medianHex(region.pixels), share: Math.round(region.pixels.length / pixels.length * 1000) / 1000 };
    }).filter(function(region) {
      return region.share > 0;
    }).sort(function(left, right) {
      return right.share - left.share || (left.colour < right.colour ? -1 : left.colour > right.colour ? 1 : 0);
    });
  }

  /**
   * What prints behind the logo: image = { data, width, height, channels }
   * (canvas RGBA, or RGB with channels: 3) of the printed background; box
   * and panel = the logo box and the back panel in its pixel units. Returns
   * { background: the box's most common colour, regions: its colours with
   * their shares (boxRegions), palette: the back panel's six dominant
   * colours, busy: no one colour covers 60% of the box } or null when the
   * box has no pixels.
   */
  function backLogoSample(image, box, panel) {
    var boxPixels = regionPixels(image, box, 1);
    if (!boxPixels.length) {
      return null;
    }
    var regions = boxRegions(boxPixels);
    var panelArea = Math.max(1, panel.width * panel.height);
    var step = Math.max(1, Math.ceil(Math.sqrt(panelArea / 16384)));
    return {
      background: regions[0].colour,
      regions: regions,
      palette: dominantColours(regionPixels(image, panel, step), 6),
      busy: regions[0].share < 0.6
    };
  }

  function hasAcceptedOverflow(coverInput) {
    var cover = normalizeCover(coverInput);
    return ["front", "back"].some(function(sideName) {
      return cover[sideName].objects.some(function(object) {
        return object.type === "text"
          && Boolean(object.overflowWarningFor)
          && object.overflowAcceptedFor === object.overflowWarningFor;
      });
    });
  }

  return {
    VERSION: VERSION,
    MAX_TEXT_OBJECTS: MAX_TEXT_OBJECTS,
    MAX_IMAGE_OBJECTS: MAX_IMAGE_OBJECTS,
    FONT_IDS: FONT_IDS.slice(),
    SEARCH_FILTER_IDS: SEARCH_FILTER_IDS.slice(),
    COLOUR_FILTER_IDS: COLOUR_FILTER_IDS.slice(),
    SHAPES: SHAPES.slice(),
    MASKS: MASKS.slice(),
    ELASTIC_CLOSURE_OFFERED: ELASTIC_CLOSURE_OFFERED,
    ELASTIC_IDS: ELASTIC_IDS.slice(),
    ELASTIC_COLOURS: clone(ELASTIC_COLOURS),
    ELASTIC_LABELS: clone(ELASTIC_LABELS),
    PRINT_RESOLUTION: clone(PRINT_RESOLUTION),
    BACK_LOGO: clone(BACK_LOGO),
    backLogoBox: backLogoBox,
    backLogoFaceBox: backLogoFaceBox,
    backLogoSample: backLogoSample,
    chooseBackLogoColour: chooseBackLogoColour,
    contrastRatio: contrastRatio,
    normalizeBackLogo: normalizeBackLogo,
    uploadBackgroundPlacement: uploadBackgroundPlacement,
    createDefaultCover: createDefaultCover,
    createTextObject: createTextObject,
    createImageObject: createImageObject,
    normalizeCover: normalizeCover,
    applyAction: applyAction,
    validateCover: validateCover,
    printResolution: printResolution,
    printResolutionStatus: printResolutionStatus,
    uploadPrintCheck: uploadPrintCheck,
    hasAcceptedOverflow: hasAcceptedOverflow,
    projectOrderCover: projectOrderCover
  };
});
